
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePdf, useSaveShareSettings } from '../../../queries/pdfs';
import ToggleSwitch from '../../../components/primitives/ToggleSwitch';
import ShareLinkBox from '../../../components/share/ShareLinkBox';
import SharePasswordField from '../../../components/share/SharePasswordField';
import ExpiryOptions from '../../../components/share/ExpiryOptions';
import SubPageHeader from '../../../components/layout/SubPageHeader';

export default function SharePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfId = searchParams.get('id');
  const { data: pdfData = null, isLoading: loading } = usePdf(pdfId);
  const shareSettingsMutation = useSaveShareSettings();
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareToken, setShareToken] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const [expiryOption, setExpiryOption] = useState<'none' | '1h' | '24h' | '7d' | '30d'>('none');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const initialTokenRef = useRef<string>('');

  useEffect(() => {
    if (!pdfId) {
      navigate('/dashboard');
      return;
    }
  }, [pdfId, navigate]);

  useEffect(() => {
    if (pdfData?.share_enabled && pdfData?.share_token) {
      setShareEnabled(true);
      setShareToken(pdfData.share_token);
      initialTokenRef.current = pdfData.share_token;
      setShareLink(`${window.location.origin}/shared/${pdfData.share_token}`);
      setUsePassword(!!pdfData.share_password);
      setHasExistingPassword(!!pdfData.share_password);
      setExpiryOption((pdfData.share_expiry as any) || 'none');
    }
  }, [pdfData]);

  const generateShareLink = () => {
    const token = crypto.randomUUID();
    setShareToken(token);
    setShareLink(`${window.location.origin}/shared/${token}`);
    setShareEnabled(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async () => {
    if (!pdfId) return;
    setSaving(true);
    setSaveError('');
    try {
      // 비밀번호: 새로 입력한 경우에만 전송, 기존 비밀번호 유지 시 null
      const passwordToSend = usePassword && sharePassword ? sharePassword : null;
      // 비밀번호 보호 해제 시 빈 문자열 대신 명시적으로 처리
      const shouldClearPassword = !usePassword && hasExistingPassword;

      await shareSettingsMutation.mutateAsync({
        pdfId: pdfId!,
        shareEnabled,
        shareToken: shareEnabled ? shareToken : null,
        sharePassword: shouldClearPassword ? '' : (passwordToSend ?? ''),
        shareExpiry: expiryOption,
        shareCreatedAt: shareEnabled ? new Date().toISOString() : null,
      });

      if (usePassword && sharePassword) {
        setHasExistingPassword(true);
        setSharePassword('');
      }
      if (!usePassword) {
        setHasExistingPassword(false);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('저장 오류:', err);
      setSaveError(t('dashboard.share_save_error'));
      setTimeout(() => setSaveError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SubPageHeader
        title={t('dashboard.share_settings')}
        subtitle={pdfData?.file_name?.replace('.pdf', '')}
        saving={saving}
        saveSuccess={saveSuccess}
        savingLabel={t('common.saving')}
        saveCompleteLabel={t('common.save_complete')}
        saveLabel={t('common.save')}
        onBack={() => navigate('/dashboard')}
        onSave={handleSave}
      />

      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* 공유 링크 생성 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <i className="ri-link text-xl text-indigo-500"></i>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">{t('dashboard.share_link')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.share_link_desc')}</p>
              </div>
            </div>
            <ToggleSwitch
              checked={shareEnabled}
              onChange={(next) => {
                if (next) {
                  generateShareLink();
                } else {
                  setShareEnabled(false);
                  setShareLink('');
                }
              }}
            />
          </div>

          {shareEnabled && shareLink && (
            <ShareLinkBox
              link={shareLink}
              copied={copied}
              onCopy={handleCopy}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            />
          )}
        </div>

        {/* 비밀번호 설정 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-lock-line text-xl text-amber-500"></i>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">{t('dashboard.password_protection')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.password_protection_desc')}</p>
              </div>
            </div>
            <ToggleSwitch checked={usePassword} onChange={setUsePassword} />
          </div>

          {usePassword && (
            <SharePasswordField
              value={sharePassword}
              onChange={setSharePassword}
              hasExistingPassword={hasExistingPassword}
              placeholderNew={t('dashboard.new_password_placeholder')}
              placeholderEnter={t('dashboard.enter_password_placeholder')}
              existingHint={t('dashboard.existing_password_hint')}
            />
          )}
        </div>

        {/* 만료 기간 설정 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <i className="ri-time-line text-xl text-emerald-500"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{t('dashboard.expiry')}</h2>
              <p className="text-xs text-gray-500">{t('dashboard.expiry_desc')}</p>
            </div>
          </div>

          <ExpiryOptions<typeof expiryOption>
            options={[
              { value: 'none', label: t('dashboard.expiry_none') },
              { value: '1h', label: t('dashboard.expiry_1h') },
              { value: '24h', label: t('dashboard.expiry_24h') },
              { value: '7d', label: t('dashboard.expiry_7d') },
              { value: '30d', label: t('dashboard.expiry_30d') },
            ]}
            value={expiryOption}
            onChange={setExpiryOption}
          />
        </div>

        {/* 에러 메시지 */}
        {saveError && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {saveError}
          </div>
        )}
      </div>
    </div>
  );
}
